import {
  NonDeliveryDates,
  NonDeliveryDatesTest3,
} from '../../config/nonDeliveryDates';
import { DateTime } from 'luxon';

export const DELIVERY_DELIVERED_TO_FRONT_DOOR =
  'DELIVERED_TO_FRONT_DOOR' as const;
export const DELIVERY_ORDER_BY_3PM_TODAY = 'ORDER_BY_3PM_TODAY' as const;
export const DELIVERY_COUNTDOWN = 'COUNTDOWN' as const;
export const DELIVERY_ORDER_BY_3PM_TOMORROW = 'ORDER_BY_3PM_TOMORROW' as const;

export type DeliveryState =
  | typeof DELIVERY_DELIVERED_TO_FRONT_DOOR
  | typeof DELIVERY_ORDER_BY_3PM_TODAY
  | typeof DELIVERY_COUNTDOWN
  | typeof DELIVERY_ORDER_BY_3PM_TOMORROW;

export function isNonDeliveryDateDt(
  dt: DateTime,
  testMode: boolean = false
): boolean {
  const key = dt.toISODate();
  const isHoliday =
    !!key &&
    (testMode
      ? NonDeliveryDatesTest3.includes(key)
      : NonDeliveryDates.includes(key));
  return isHoliday;
}

export function getDeliveryState(now: Date | DateTime): DeliveryState {
  let dt: DateTime;
  if (now instanceof Date) {
    dt = DateTime.fromJSDate(now);
  } else {
    dt = now;
  }

  const hour = dt.hour;
  const minute = dt.minute;
  const dayOfWeek = dt.weekday % 7;

  if (isNonDeliveryDateDt(dt)) return DELIVERY_DELIVERED_TO_FRONT_DOOR;

  if (isNonDeliveryDateDt(dt.plus({ days: 1 })))
    return DELIVERY_DELIVERED_TO_FRONT_DOOR;

  const isTwoDaysBefore = isNonDeliveryDateDt(dt.plus({ days: 2 }));
  const afterThree = hour > 15 || (hour === 15 && minute >= 0);
  if (isTwoDaysBefore && afterThree) return DELIVERY_DELIVERED_TO_FRONT_DOOR;

  if (dayOfWeek === 6 || dayOfWeek === 0)
    return DELIVERY_DELIVERED_TO_FRONT_DOOR; // Saturday and Sunday.

  if (dayOfWeek === 5 && afterThree) {
    return DELIVERY_DELIVERED_TO_FRONT_DOOR; // Friday after 3pm.
  }

  if (hour < 12) return DELIVERY_ORDER_BY_3PM_TODAY;
  if (hour >= 12 && hour < 15) return DELIVERY_COUNTDOWN;

  return DELIVERY_ORDER_BY_3PM_TOMORROW;
}

function getDate(date: Date | DateTime): DateTime | null {
  let dt: DateTime;
  if (DateTime.isDateTime(date)) {
    if (!date.isValid) return null;
    dt = date.setZone('Europe/London');
  } else if (date instanceof Date && !Number.isNaN(date.getTime())) {
    dt = DateTime.fromJSDate(date, { zone: 'Europe/London' });
  } else {
    return null;
  }
  return dt;
}

const dayNames = [
  'Sunday',
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
];

export function getNextDay(date: Date | DateTime): string | null {
  const dt: DateTime | null = getDate(date);
  if (!dt) return null;

  const hour = dt.hour;
  if (hour < 12 || hour >= 15) return '';

  const today = dt.weekday % 7;
  if (today === 6 || today === 7) return ''; // exclude Saturday and Sunday

  let nextDayIndex = (today + 1) % 7;
  return dayNames[nextDayIndex];
}

// Saturday is a valid collection day. Only Sunday is excluded as a non-collection weekend day.
function isNonDeliveryWeekend(dt: DateTime): boolean {
  const wd = dt.weekday % 7;
  return wd === 0; // Sunday only
}

// Returns the first Mon–Fri non-bank-holiday day available for order processing.
// Orders placed after 3pm cannot be processed until the following calendar day.
function getEffectiveProcessingDay(
  currentDate: DateTime,
  isAfterThree: boolean
): DateTime {
  let processingDay = isAfterThree
    ? currentDate.plus({ days: 1 })
    : currentDate;
  while (
    processingDay.weekday % 7 === 0 || // Sunday
    processingDay.weekday % 7 === 6 || // Saturday
    isNonDeliveryDateDt(processingDay, true) // bank holiday
  ) {
    processingDay = processingDay.plus({ days: 1 });
  }
  return processingDay;
}

export function getNextDeliveryOrCollectionDay(
  date: Date | DateTime
): string | null {
  const currentDate = getDate(date);
  if (!currentDate) return null;

  const hour = currentDate.hour;
  const minute = currentDate.minute;
  const isAfterThree = hour > 15 || (hour === 15 && minute >= 0);

  const todayIndex = currentDate.weekday % 7;

  let daysAhead = 1;

  // The collection/delivery day must always be strictly after the processing day.
  // This single rule handles all bank holiday and Saturday edge cases.
  const effectiveProcessingDay = getEffectiveProcessingDay(
    currentDate,
    isAfterThree
  );

  // Helper to check if candidate day is valid for delivery/collection
  function isValidCandidate(candidateDatetime: DateTime): boolean {
    const isCandidateHoliday = isNonDeliveryDateDt(candidateDatetime, true);
    const isCandidateWeekend = isNonDeliveryWeekend(candidateDatetime);
    return !isCandidateHoliday && !isCandidateWeekend;
  }

  // Loop to find the next valid delivery/collection day
  let isFindingNextDeliveryDay = true;
  while (isFindingNextDeliveryDay) {
    const candidateDatetime = currentDate.plus({ days: daysAhead });

    if (
      candidateDatetime.startOf('day').toMillis() <=
      effectiveProcessingDay.startOf('day').toMillis()
    ) {
      daysAhead++;
      continue;
    }

    if (isValidCandidate(candidateDatetime)) {
      isFindingNextDeliveryDay = false;
      break;
    }

    daysAhead++;
  }

  return daysAhead === 1 ? 'tomorrow' : dayNames[(todayIndex + daysAhead) % 7];
}

function isNonOrderWeekend(dt: DateTime): boolean {
  const wd = dt.weekday % 7;
  return wd === 6 || wd === 0; //skip Saturday and Sunday as order days
}

export function getNextOrderDate(date: Date | DateTime):
  | {
      orderDate: DateTime;
      orderDayName: string;
    }
  | null
  | undefined {
  let currentDate: DateTime | null = getDate(date);
  if (!currentDate) return null;

  currentDate = currentDate.setZone('Europe/London');

  let daysAhead = 0;

  let isFindingNextOrderDay = true;
  while (isFindingNextOrderDay) {
    let candidateDatetime = currentDate.plus({ days: daysAhead }).set({
      hour: 15,
      minute: 0,
      second: 0,
      millisecond: 0,
    });

    if (
      candidateDatetime.toMillis() <= currentDate.toMillis() || // cutoff already passed (only possible when daysAhead === 0)
      isNonDeliveryDateDt(candidateDatetime, true) ||
      isNonOrderWeekend(candidateDatetime)
    ) {
      daysAhead++;
      continue;
    }

    const candidateDayIndex = candidateDatetime.weekday % 7;
    const candidateDayName = dayNames[candidateDayIndex];
    isFindingNextOrderDay = false;

    return {
      orderDate: candidateDatetime,
      orderDayName: candidateDayName,
    };
  }
}

export function getToday3pm(date: Date | DateTime): string | null {
  let dt: DateTime | null = getDate(date);
  if (!dt) return null;

  const hour = dt.hour;
  if (hour < 12 || hour >= 15) return null;

  const at3pm = dt.set({ hour: 15, minute: 0, second: 0, millisecond: 0 });
  return at3pm.toISO({
    suppressMilliseconds: true,
    suppressSeconds: true,
  });
}
